import React, { useState, useEffect } from 'react';

const PacientePanel = () => {
  const [triageData, setTriageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [redirectCountdown, setRedirectCountdown] = useState(null); 
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [esMobil, setEsMobil] = useState(window.innerWidth < 768);
  const [ipBase, setIpBase] = useState(() => localStorage.getItem('ipBase') || '172.20.10.9');
  const [ipTemporal, setIpTemporal] = useState(() => localStorage.getItem('ipBase') || '172.20.10.9');
  const [mostrarAccesoIP, setMostrarAccesoIP] = useState(false);
  const [mostrarModalIP, setMostrarModalIP] = useState(false);
  const [claveIP, setClaveIP] = useState('');
  const CLAVE_ADMIN = 'Vital2026';

  // OBTENER DATOS DIRECTAMENTE DEL LOCALSTORAGE (GUARDADOS DURANTE EL LOGIN EXITOSO)
  const dniRaw = localStorage.getItem('dniUsuario');
  const dni = dniRaw ? dniRaw.replace(/['"]+/g, '').trim() : null;
  
  const idPaciente = localStorage.getItem('idPaciente');
  const nombrePaciente = localStorage.getItem('nombrePaciente');
  const apellidoPaciente = localStorage.getItem('apellidoPaciente');

  // Estructura de datos del paciente cargada localmente en producción
  const patientInfo = idPaciente ? { 
    id_paciente: idPaciente, 
    nombre: nombrePaciente, 
    apellido: apellidoPaciente, 
    dni: dni 
  } : null;

  useEffect(() => {
    const handleResize = () => setEsMobil(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // SEGURIDAD: Si no existe sesión válida del paciente, redirige inmediatamente al Login sin parpadear
  useEffect(() => {
    if (!dni || !idPaciente) {
      window.location.replace('/');
    }
  }, [dni, idPaciente]);

  useEffect(() => {
    if (redirectCountdown === null) return;
    
    if (redirectCountdown <= 0) {
      // Limpieza segura de datos al expirar el tiempo de visualización
      localStorage.removeItem('dniUsuario');
      localStorage.removeItem('idPaciente');
      localStorage.removeItem('nombrePaciente');
      localStorage.removeItem('apellidoPaciente');
      localStorage.removeItem('rol'); 
      window.location.replace('/'); 
      return;
    }

    const timer = setTimeout(() => {
      setRedirectCountdown(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [redirectCountdown]);

  const sendTriageData = async (data) => {
    try {
      if (!data.id_paciente || !data.dni_paciente) {
        console.error('Datos incompletos. Cancelando envío.');
        return;
      }
      const response = await fetch(
        'https://backend-46yr.onrender.com/api/triaje',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      );
      if (!response.ok) {
        throw new Error(`Error al guardar triaje: ${response.status}`);
      }
      const result = await response.json();
      console.log('Datos guardados exitosamente en la nube:', result);
    } catch (err) {
      console.error('Error enviando datos:', err);
    }
  };

  const fetchTriageData = async () => {
    if (!dni || !idPaciente) {
      setError('No se puede iniciar la medición: Sesión inválida.');
      return;
    }
    if (loading || isMeasuring) return;

    setIsMeasuring(true);
    setLoading(true);
    setError(null);
    setTriageData(null); 
    setRedirectCountdown(null); 
    setCountdown(3);
    setProgreso(0);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    let valor = 0;
    const progressInterval = setInterval(() => {
      valor += 10;
      setProgreso(valor);
      if (valor >= 100) {
        clearInterval(progressInterval);
      }
    }, 300);

    setTimeout(async () => {
      try {
        const response = await fetch(`http://${ipBase}/datos`);
        if (!response.ok) {
          throw new Error(`El sensor no responde en la IP ${ipBase}: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.triage === 'ESPERANDO') {
          setError('Por favor coloque correctamente el dedo y vuelva a intentar');
          setTriageData(null);
          setLoading(false);        
          setIsMeasuring(false);    
          return;                   
        }

        // Lógica optimizada: Ya no hacemos un fetch redundante a la API por DNI, usamos patientInfo local
        const enrichedData = {
          ...data,
          id_paciente: idPaciente,
          dni_paciente: dni,
          timestamp: new Date().toISOString()
        };

        setTriageData(enrichedData);
        await sendTriageData(enrichedData);
        
        setRedirectCountdown(8);

      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally { 
        setLoading(false);
        setIsMeasuring(false);
      }
    }, 3000);
  };

  const validarAccesoIP = () => {
    if (claveIP === CLAVE_ADMIN) {
      setMostrarAccesoIP(false);
      setMostrarModalIP(true);
      setClaveIP('');
    } else {
      alert('Contraseña incorrecta. Acceso denegado.');
      setClaveIP('');
    }
  };

  const guardarNuevaIP = () => {
    if (!ipTemporal.trim()) {
      alert('Por favor ingrese una IP válida.');
      return;
    }
    setIpBase(ipTemporal);
    localStorage.setItem('ipBase', ipTemporal);
    setMostrarModalIP(false);
    alert(`Dirección IP cambiada con éxito a: ${ipTemporal}`);
  };

  // Spinner de contingencia si los datos no cargaron desde localStorage
  if (!patientInfo) {
    return (
      <div style={{ ...estilos.pagina, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a6b8a', fontWeight: 'bold' }}>
        Verificando sesión del paciente...
      </div>
    );
  }

  const radio = 54;
  const circunferencia = 2 * Math.PI * radio;
  const offset = circunferencia - (progreso / 100) * circunferencia;

  return (
    <div style={estilos.pagina}>
      <div style={estilos.header}>
        <div style={estilos.headerIzq}>
          <span style={{ fontSize: '24px' }}>⚕️</span>
          <span style={estilos.logoTexto}>VitalScan</span>
        </div>
        
        <button 
          onClick={() => setMostrarAccesoIP(true)} 
          style={estilos.botonConfiguracion}
          title="Configurar IP del dispositivo"
        >
          ⚙️ 
        </button>
      </div>

      <div style={{ ...estilos.cuerpo, flexDirection: esMobil ? 'column' : 'row' }}>
        {/* Panel Izquierdo */}
        <div style={{ ...estilos.panelIzquierdo, padding: esMobil ? '28px 20px' : '48px 44px' }}>
          <div style={estilos.panelIzqInner}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🫳</div>
            <h2 style={estilos.panelTitulo}>Mida sus signos vitales</h2>
            
            {patientInfo && (
              <p style={{ color: '#a5f3fc', fontSize: '16px', margin: '-5px 0 15px 0', fontWeight: 'bold' }}>
                Paciente: {patientInfo.nombre} {patientInfo.apellido}
              </p>
            )}
            
            <p style={estilos.panelDesc}>
              Coloque su dedo en el sensor y espere unos segundos.
            </p>
            <button
              onClick={fetchTriageData}
              disabled={loading || isMeasuring}
              style={{
                ...estilos.boton,
                opacity: loading || isMeasuring ? 0.7 : 1
              }}
            >
              {isMeasuring ? `Midiendo... ${countdown}s` : 'Iniciar Medición'}
            </button>
          </div>
        </div>

        {/* Panel Derecho */}
        <div style={estilos.panelDerecho}>
          <div style={estilos.circuloContenedor}>
            <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
              <circle
                cx="70" cy="70" r={radio}
                fill="none" stroke="#e2e8f0" strokeWidth="10"
              />
              <circle
                cx="70" cy="70" r={radio}
                fill="none"
                stroke={
                  triageData?.triage?.toLowerCase() === 'rojo'
                    ? '#dc2626'
                    : triageData?.triage?.toLowerCase() === 'amarillo'
                      ? '#d97706'
                      : '#1a6b8a'
                }
                strokeWidth="10"
                strokeDasharray={circunferencia}
                strokeDashoffset={offset}
                strokeLinecap="round"
              />
            </svg>
            <div style={estilos.circuloTexto}>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#1a6b8a', textAlign: 'center', width: '120px', display: 'block' }}>
                {loading || isMeasuring
                  ? 'Midiendo...'
                  : redirectCountdown !== null
                    ? `Saliendo en ${redirectCountdown}s...`
                    : triageData
                      ? `Triage ${triageData.triage}`
                      : 'Esperando medición'
                }
              </span>
            </div>
          </div>

          {error && <div style={estilos.error}>{error}</div>}

          <div style={{ ...estilos.tarjetasFila, flexDirection: esMobil ? 'column' : 'row' }}>
            {/* Temperatura */}
            <div style={{ ...estilos.tarjeta, background: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <span style={estilos.icono}>🌡️</span>
              <div style={estilos.valorContainer}>
                <span style={{ ...estilos.valor, color: '#d97706' }}>
                  {triageData?.temperatura ?? '--'}
                </span>
                <span style={{ ...estilos.unidad, color: '#d97706' }}>°C</span>
              </div>
              <p style={estilos.label}>Temperatura corporal</p>
            </div>

            {/* Pulso */}
            <div style={{ ...estilos.tarjeta, background: '#fef2f2', border: '1.5px solid #fca5a5' }}>
              <span style={estilos.icono}>❤️</span>
              <div style={estilos.valorContainer}>
                <span style={{ ...estilos.valor, color: '#dc2626' }}>
                  {triageData?.pulso ?? '--'}
                </span>
                <span style={{ ...estilos.unidad, color: '#dc2626' }}>bpm</span>
              </div>
              <p style={estilos.label}>Pulso</p>
            </div>

            {/* Oxigenación */}
            <div style={{ ...estilos.tarjeta, background: '#f0f9ff', border: '1.5px solid #bae6fd' }}>
              <span style={estilos.icono}>💧</span>
              <div style={estilos.valorContainer}>
                <span style={{ ...estilos.valor, color: '#0284c7' }}>
                  {triageData?.spo2 ?? '--'}
                </span>
                <span style={{ ...estilos.unidad, color: '#0284c7' }}>%</span>
              </div>
              <p style={estilos.label}>Oxigenación</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal - Autenticación Admin para Cambiar IP */}
      {mostrarAccesoIP && (
        <div style={estilos.overlayModal}>
          <div style={estilos.modalContenedor}>
            <h3 style={estilos.modalTitulo}>Acceso Restringido</h3>
            <p style={estilos.modalTexto}>Ingrese clave de administrador para configurar la IP del sensor:</p>
            <input 
              type="password" 
              value={claveIP} 
              onChange={(e) => setClaveIP(e.target.value)} 
              style={estilos.modalInput}
              placeholder="Contraseña"
            />
            <div style={estilos.modalAcciones}>
              <button onClick={() => setMostrarAccesoIP(false)} style={estilos.botonCancelar}>Cancelar</button>
              <button onClick={validarAccesoIP} style={estilos.botonAceptar}>Verificar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Configuración Direccion IP Real */}
      {mostrarModalIP && (
        <div style={estilos.overlayModal}>
          <div style={estilos.modalContenedor}>
            <h3 style={estilos.modalTitulo}>Configurar IP del Dispositivo</h3>
            <p style={estilos.modalTexto}>IP actual: <strong>{ipBase}</strong></p>
            <input 
              type="text" 
              value={ipTemporal} 
              onChange={(e) => setIpTemporal(e.target.value)} 
              style={estilos.modalInput}
              placeholder="Ej: 192.168.1.50"
            />
            <div style={estilos.modalAcciones}>
              <button onClick={() => setMostrarModalIP(false)} style={estilos.botonCancelar}>Cerrar</button>
              <button onClick={guardarNuevaIP} style={estilos.botonAceptar}>Guardar Cambios</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Paleta de estilos CSS-in-JS coherente con la interfaz VitalScan
const estilos = {
  pagina: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#f8fafc'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0f172a',
    padding: '16px 40px',
    color: '#fff'
  },
  headerIzq: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  logoTexto: {
    fontSize: '22px',
    fontWeight: 'bold',
    letterSpacing: '0.5px'
  },
  botonConfiguracion: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    fontSize: '18px',
    padding: '10px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  cuerpo: {
    display: 'flex',
    flex: 1
  },
  panelIzquierdo: {
    backgroundColor: '#1e3a8a',
    color: '#fff',
    flex: '1 1 35%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)'
  },
  panelIzqInner: {
    maxWidth: '360px',
    textAlign: 'left'
  },
  panelTitulo: {
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '12px',
    lineHeight: '1.2'
  },
  panelDesc: {
    color: '#93c5fd',
    fontSize: '15px',
    lineHeight: '1.5',
    marginBottom: '28px'
  },
  boton: {
    backgroundColor: '#fff',
    color: '#1e3a8a',
    border: 'none',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: '8px',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
    transition: 'transform 0.1s'
  },
  panelDerecho: {
    flex: '1 1 65%',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '30px'
  },
  circuloContenedor: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  circuloTexto: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    padding: '12px 24px',
    borderRadius: '8px',
    border: '1.5px solid #fca5a5',
    fontWeight: '500',
    fontSize: '14px',
    textAlign: 'center',
    maxWidth: '500px'
  },
  tarjetasFila: {
    display: 'flex',
    gap: '20px',
    width: '100%',
    maxWidth: '750px',
    justifyContent: 'center'
  },
  tarjeta: {
    flex: 1,
    borderRadius: '12px',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    minWidth: '160px'
  },
  icono: {
    fontSize: '28px',
    marginBottom: '12px'
  },
  valorContainer: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '2px',
    marginBottom: '6px'
  },
  valor: {
    fontSize: '36px',
    fontWeight: 'bold'
  },
  unidad: {
    fontSize: '16px',
    fontWeight: '600'
  },
  label: {
    color: '#64748b',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
    margin: 0
  },
  overlayModal: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContenedor: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
  },
  modalTitulo: {
    margin: '0 0 10px 0',
    fontSize: '18px',
    color: '#0f172a'
  },
  modalTexto: {
    fontSize: '14px',
    color: '#475569',
    marginBottom: '20px',
    lineHeight: '1.4'
  },
  modalInput: {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: '1.5px solid #cbd5e1',
    fontSize: '15px',
    marginBottom: '24px',
    boxSizing: 'border-box'
  },
  modalAcciones: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  botonCancelar: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  botonAceptar: {
    backgroundColor: '#1e3a8a',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default PacientePanel;